// src/components/common/StaffMemberPicker.jsx
import React, { useEffect, useCallback } from 'react';
import { Autocomplete, Box, InputAdornment, TextField, Typography } from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { useStaff } from '../../features/staff/hooks/useStaff';
import ProfilePicture from './ProfilePicture';

/**
 * Standalone staff member picker backed by the department staff list.
 *
 * Props:
 *  value        — selected staff_member_id string (or null)
 *  onChange     — (staff_member_id | null) => void
 *  label        — TextField label
 *  required     — mark the field as required (asterisk on the label)
 *  placeholder  — TextField placeholder
 *  error        — boolean
 *  helperText   — string
 *  disabled     — boolean
 *  size         — 'small' | 'medium'  (default 'small')
 *  excludeIds   — string[]  staff_member_ids to hide from the list
 *  sx           — MUI sx prop forwarded to the Autocomplete root
 */
export default function StaffMemberPicker({
  value = null,
  onChange,
  label,
  placeholder,
  error = false,
  helperText,
  disabled = false,
  required = false,
  size = 'small',
  excludeIds = [],
  sx,
}) {
  const { staff, loading, fetchDepartmentStaff } = useStaff();

  useEffect(() => {
    fetchDepartmentStaff({ include_inactive: false });
  }, [fetchDepartmentStaff]);

  const options = excludeIds.length
    ? staff.filter((s) => !excludeIds.includes(s.staff_member_id))
    : staff;

  const selected = staff.find((s) => s.staff_member_id === value) ?? null;

  const handleChange = useCallback(
    (_event, option) => {
      onChange(option?.staff_member_id ?? null);
    },
    [onChange],
  );

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option) => option.name}
      value={selected}
      onChange={handleChange}
      loading={loading}
      disabled={disabled || loading}
      size={size}
      sx={sx}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start">
                  {selected ? (
                    <ProfilePicture
                      profilePictureFileId={selected.profile_picture_file_id}
                      name={selected.name}
                      size={24}
                      imageSize="32x32"
                      bgcolor="grey.400"
                      color={selected?.color}
                    />
                  ) : (
                    <PersonIcon color="disabled" fontSize="small" />
                  )}
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={({ key, ...props }, option) => (
        <Box component="li" key={key} {...props}>
          <ProfilePicture
            profilePictureFileId={option.profile_picture_file_id}
            name={option.name}
            size={36}
            imageSize="64x64"
            bgcolor="grey.400"
            color={option?.color}
            sx={{ mr: 1.5, flexShrink: 0 }}
          />
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {option.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {option.staff_member_id.substring(0, 8)}…
            </Typography>
          </Box>
        </Box>
      )}
    />
  );
}
